import { observer } from "mobx-react-lite";
import React from "react";
import { StyleSheet } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { styleConstants as C } from "~/style/styleConstants";
import { shadow } from "~/utils/shadow";
import { IconButton } from "./IconButton";
import { Text } from "./Text";
import { View } from "./View";
import type { BottomTabHeaderProps } from "@react-navigation/bottom-tabs";
import type { NativeStackHeaderProps } from "@react-navigation/native-stack";

export const headerHeight = 52;

const S = StyleSheet.create({
  container: {
    ...shadow(5),
  },
  headerContainer: {
    height: headerHeight,
    backgroundColor: C.colorBackgroundLight,
    justifyContent: "space-between",
  },
  backButton: {
    height: headerHeight,
    width: headerHeight,
    justifyContent: "center",
    alignItems: "center",
  },
  headerLeft: { position: "absolute", top: 0, left: 0, bottom: 0 },
  headerRight: { position: "absolute", top: 0, right: 0, bottom: 0 },
  titleText: { flex: 1 },
});

type HeaderProps = (NativeStackHeaderProps | BottomTabHeaderProps) & {
  options: {
    hideBackButton?: boolean | undefined;
  };
};

export const Header = observer(
  ({ options, navigation, ...props }: HeaderProps) => {
    const canGoBack = "back" in props && !!props.back;
    const insets = useSafeAreaInsets();

    const HeaderRight = options?.headerRight?.({ canGoBack });
    const HeaderLeft = options?.headerLeft?.({ canGoBack });

    const { title, hideBackButton = false } = options;

    const statusBarBackground = (
      <View
        style={{
          height: insets.top,
          backgroundColor: "transparent",
        }}
      />
    );

    return (
      <View style={S.container}>
        {statusBarBackground}
        <View flexDirectionRow alignItemsCenter style={S.headerContainer}>
          <View pointerEventsNone centerContent flex>
            <Text
              alignCenter
              colorDark
              sizeMedium
              weightSemiBold
              ellipsizeMode="tail"
              numberOfLines={1}
            >
              {title}
            </Text>
          </View>

          <View alignItemsCenter flexDirectionRow style={S.headerLeft}>
            {canGoBack && !hideBackButton && (
              <IconButton
                style={S.backButton}
                onPress={() => {
                  navigation.goBack();
                }}
                iconName="arrow-back"
                iconSize={28}
                iconColor={C.colorTextDark}
              />
            )}

            {HeaderLeft}
          </View>

          <View alignItemsCenter flexDirectionRow style={S.headerRight}>
            {HeaderRight}
          </View>
        </View>
      </View>
    );
  },
);
